/**
 * Operator selector UI component
 */

import { useState, useEffect } from 'react';
import { OPERATORS_WITH_TRACKING, POLL_INTERVALS, RATE_LIMIT_INFO } from '../config';
import type { Operator } from '../config';
import { getVehicleType } from '../utils/vehicleTypes';
import './OperatorSelector.css';

interface OperatorSelectorProps {
  selectedOperator: string;
  onOperatorChange: (operatorId: string) => void;
  vehicleCount: number;
  lastUpdate: Date | null;
  isLoading: boolean;
  pollInterval: number;
  onPollIntervalChange: (interval: number) => void;
  onSettingsClick: () => void;
  showRoutes: boolean;
  onShowRoutesChange: (show: boolean) => void;
  availableRouteTypes: Set<string | number>;
  visibleRouteTypes: Set<string | number>;
  onRouteTypeToggle: (type: string | number) => void;
  showLabels: boolean;
  onShowLabelsChange: (show: boolean) => void;
}

export function OperatorSelector({
  selectedOperator,
  onOperatorChange,
  vehicleCount,
  lastUpdate,
  isLoading,
  pollInterval,
  onPollIntervalChange,
  onSettingsClick,
  showRoutes,
  onShowRoutesChange,
  availableRouteTypes,
  visibleRouteTypes,
  onRouteTypeToggle,
  showLabels,
  onShowLabelsChange
}: OperatorSelectorProps) {
  const operators = OPERATORS_WITH_TRACKING;
  const [timeUntilNext, setTimeUntilNext] = useState<number>(0);

  // Calculate estimated monthly requests
  const requestsPerMonth = Math.floor((30 * 24 * 60 * 60 * 1000) / pollInterval);
  const bronzeLimit = RATE_LIMIT_INFO.BRONZE.limit;
  const isOverLimit = requestsPerMonth > bronzeLimit;

  // Update countdown timer every second
  useEffect(() => {
    const updateCountdown = () => {
      if (!lastUpdate) {
        setTimeUntilNext(pollInterval / 1000);
        return;
      }

      const nextUpdateTime = lastUpdate.getTime() + pollInterval;
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((nextUpdateTime - now) / 1000));

      setTimeUntilNext(remaining);
    };

    // Update immediately
    updateCountdown();

    // Then update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [lastUpdate, pollInterval]);

  const formatCountdown = (seconds: number): string => {
    if (isLoading) {
      return 'updating...';
    }

    if (seconds === 0) {
      return 'now';
    }

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  return (
    <div className="operator-selector">
      <div className="selector-header">
        <h2>Transit Track</h2>
        <button
          className="settings-button"
          onClick={onSettingsClick}
          title="API Settings"
        >
          ⚙️
        </button>
      </div>

      <div className="selector-content">
        <label htmlFor="operator-select">Operator:</label>
        <select
          id="operator-select"
          value={selectedOperator}
          onChange={(e) => onOperatorChange(e.target.value)}
          disabled={isLoading}
        >
          {operators.map((op: Operator) => (
            <option key={op.id} value={op.id}>
              {op.name}
            </option>
          ))}
        </select>

        <label htmlFor="interval-select">Update Interval:</label>
        <select
          id="interval-select"
          value={pollInterval}
          onChange={(e) => onPollIntervalChange(parseInt(e.target.value))}
          disabled={isLoading}
          className={isOverLimit ? 'warning' : ''}
        >
          <option value={POLL_INTERVALS.REALTIME}>10 seconds</option>
          <option value={POLL_INTERVALS.FREQUENT}>30 seconds</option>
          <option value={POLL_INTERVALS.NORMAL}>1 minute</option>
          <option value={POLL_INTERVALS.MODERATE}>2 minutes</option>
          <option value={POLL_INTERVALS.CONSERVATIVE}>3 minutes</option>
          <option value={POLL_INTERVALS.MINIMAL}>5 minutes</option>
        </select>

        {isOverLimit && (
          <div className="rate-warning">
            ⚠️ ~{requestsPerMonth.toLocaleString()} req/mo exceeds Bronze limit
          </div>
        )}

        <div className="route-controls">
          <label className="checkbox-label master-toggle">
            <input
              type="checkbox"
              checked={showRoutes}
              onChange={(e) => onShowRoutesChange(e.target.checked)}
            />
            <span>Show route paths</span>
          </label>

          {showRoutes && availableRouteTypes.size > 0 && (
            <div className="route-type-toggles">
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px', fontWeight: 500 }}>Filter by vehicle type:</div>
              {Array.from(availableRouteTypes)
                .sort((a, b) => {
                  // Sort by display name for consistency
                  const typeA = getVehicleType(a);
                  const typeB = getVehicleType(b);
                  return typeA.displayName.localeCompare(typeB.displayName);
                })
                .map((type) => {
                  const vehicleType = getVehicleType(type);
                  return (
                    <label key={type} className="checkbox-label type-toggle" title={vehicleType.description}>
                      <input
                        type="checkbox"
                        checked={visibleRouteTypes.has(type)}
                        onChange={() => onRouteTypeToggle(type)}
                      />
                      <span>
                        {vehicleType.displayName}
                      </span>
                    </label>
                  );
                })}
            </div>
          )}
        </div>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showLabels}
            onChange={(e) => onShowLabelsChange(e.target.checked)}
          />
          <span>Show vehicle labels</span>
        </label>

        <div className="stats">
          <div className="stat">
            <span className="stat-label">Vehicles:</span>
            <span className="stat-value">{vehicleCount}</span>
          </div>

          {lastUpdate && (
            <div className="stat">
              <span className="stat-label">Updated:</span>
              <span className="stat-value">
                {lastUpdate.toLocaleTimeString()}
              </span>
            </div>
          )}

          <div className="stat">
            <span className="stat-label">Next update:</span>
            <span className="stat-value countdown">
              {formatCountdown(timeUntilNext)}
            </span>
          </div>

          <div className="stat">
            <span className={`status-indicator ${isLoading ? 'loading' : 'connected'}`}>
              {isLoading ? '⟳' : '●'}
            </span>
            <span className="stat-value">
              {isLoading ? 'Updating...' : 'Live'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
